import { lockScroll, unlockScroll } from '@theme/utilities';

/** The drawer this applies to. Other theme drawers keep Horizon's behaviour. */
const CART_DRAWER = 'cart-drawer';

/**
 * Makes the cart drawer overlay the page instead of pushing it aside.
 *
 * Horizon runs its drawers in two modes: under 990px the panel is a modal
 * dialog with a native backdrop and a scroll lock, and at 990px and up it is a
 * non-modal dialog that squeezes the page sideways — `.page-wrapper` gets a
 * right margin and the page stays scrollable. The design calls for the same
 * overlay at every width: the panel on top, the page dimmed behind it and the
 * page scroll held still.
 *
 * Done from the outside rather than by editing Horizon's theme-drawer, for two
 * reasons: that component drives every drawer in the theme, not just the cart,
 * and a base file changed here is a file that silently loses the change on the
 * next Horizon update (CLAUDE.md red line #8).
 *
 * So this marks the root element while the cart is open — the CSS in
 * assets/setbox-design.css cancels the squeeze and draws the backdrop — and
 * borrows Horizon's own refcounted lockScroll, which is what sets the
 * `scroll-lock` attribute base.css already acts on. Using the drawer element
 * as the lock owner keeps our lock separate from the one the component takes
 * in modal mode, so the two cannot unbalance each other.
 */
const root = document.documentElement;
const panelOf = (drawer) => drawer?.querySelector('dialog');

document.addEventListener('theme-drawer:open', (event) => {
  const drawer = event.target;
  if (!(drawer instanceof HTMLElement) || drawer.id !== CART_DRAWER) return;
  root.setAttribute('data-setbox-cart-open', '');
  lockScroll(drawer);
});

document.addEventListener('theme-drawer:close', (event) => {
  const drawer = event.target;
  if (!(drawer instanceof HTMLElement) || drawer.id !== CART_DRAWER) return;
  root.removeAttribute('data-setbox-cart-open');
  unlockScroll(drawer);
});

/**
 * "Continue shopping" closes the drawer rather than navigating.
 *
 * Horizon's empty-cart state links that button at a collection, which is right
 * on the cart page and wrong in a drawer: the shopper is already on the page
 * they wanted, and sending them to a collection throws that away. Handled here
 * rather than by editing snippets/cart-products.liquid, so the button still
 * navigates where it should — on /cart, outside this drawer.
 */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest('.cart-items__empty-button');
  const drawer = document.getElementById(CART_DRAWER);
  if (!button || !drawer?.contains(button)) return;

  event.preventDefault();
  // @ts-expect-error — close() is theme-drawer's public API.
  drawer.close?.();
});

/**
 * Closing by clicking the dim area.
 *
 * In modal mode the browser gives the dialog a real ::backdrop and Horizon's
 * own handler on the panel catches those clicks. Our backdrop is painted by
 * the root element, so a click on it never reaches the panel and has to be
 * caught here.
 */
document.addEventListener('click', (event) => {
  if (!root.hasAttribute('data-setbox-cart-open')) return;

  const drawer = document.getElementById(CART_DRAWER);
  const panel = panelOf(drawer);
  if (!panel || !(event.target instanceof Element)) return;

  // Inside the panel, or on the control that opens it — the opening click
  // bubbles up to here in the same tick and would close it again.
  if (panel.contains(event.target)) return;
  if (event.target.closest(`[aria-controls="${CART_DRAWER}"]`)) return;

  // @ts-expect-error — close() is theme-drawer's public API.
  drawer.close?.();
});
